import { useReorderQuotePhasesMutation } from "@common/api/quoting/quote-phase"
import type { QuoteDetailPublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import { handleError } from "@common/utils"
import { Link, useNavigate } from "@tanstack/react-router"
import { PhaseMenu } from "@/components/phase/phase-menu"
import useCustomToast from "@/hooks/use-custom-toast"
import type { GroupLabel } from "@/lib/grouping"
import { movedPhase } from "@/lib/phases"
import { useEditable } from "../-lib/editable"

export function NewPhaseButton({
  quote,
}: {
  quote: QuoteDetailPublic
}): React.ReactElement {
  const editable = useEditable(quote)

  if (!editable) {
    return (
      <Button size="sm" variant="outline" disabled>
        New phase
      </Button>
    )
  }
  return (
    <Button size="sm" variant="outline" asChild>
      <Link
        to="/quotes/$quoteId/phases/new"
        params={{ quoteId: quote.id }}
        search={true}
      >
        New phase
      </Link>
    </Button>
  )
}

export function PhaseGroupActions({
  quote,
  section,
}: {
  quote: QuoteDetailPublic
  section: GroupLabel
}): React.ReactElement | null {
  const editable = useEditable(quote)
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const reorder = useReorderQuotePhasesMutation()
  const phases = quote.active_phases
  const index = phases.findIndex((phase) => phase.id === section.key)
  const params = { quoteId: quote.id, phaseId: section.key }

  if (!editable || index < 0) return null

  return (
    <PhaseMenu
      name={section.label}
      first={index === 0}
      last={index === phases.length - 1}
      disabled={reorder.isPending}
      onRename={() =>
        navigate({
          to: "/quotes/$quoteId/phases/$phaseId/rename",
          params,
          search: true,
        })
      }
      onMove={(offset) =>
        reorder.mutate(
          {
            quote_id: quote.id,
            quotePhaseOrder: {
              phase_ids: movedPhase(phases, section.key, offset),
            },
          },
          { onError: (error) => handleError(error, showToast) },
        )
      }
      onDelete={() =>
        navigate({
          to: "/quotes/$quoteId/phases/$phaseId/delete",
          params,
          search: true,
        })
      }
    />
  )
}
